import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Heart, Star } from 'lucide-react';
import { Button } from './ui/button';
import { useFavoriteProperty } from '../hooks/useFavoriteProperty';
import { thumbUrl } from '../utils/imageUrl';
import { getPropertyBadges } from '../utils/propertyBadges';

const PLACEHOLDER_IMG =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600' preserveAspectRatio='xMidYMid slice'>
      <defs>
        <linearGradient id='g' x1='0' x2='1' y1='0' y2='1'>
          <stop offset='0' stop-color='%23f4f4f5'/>
          <stop offset='1' stop-color='%23e4e4e7'/>
        </linearGradient>
      </defs>
      <rect width='600' height='600' fill='url(%23g)'/>
      <g fill='%23a1a1aa' transform='translate(220 220)'>
        <path d='M0 80V0h160v80zm10-10h140V10H10zm20-20l30-40 30 40 20-30 30 30zM40 30a10 10 0 1 1 0-20 10 10 0 0 1 0 20z'/>
      </g>
      <text x='300' y='420' text-anchor='middle' font-family='system-ui,sans-serif' font-size='22' fill='%2371717a'>Photo unavailable</text>
    </svg>`,
  );

const PropertyCard = ({ property, onClick }) => {
  const { t } = useTranslation();
  const propertyId = property?._id || property?.id;
  const { isFavorite, toggle: toggleFavorite } = useFavoriteProperty(propertyId);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Track which images in this property's array have already failed to
  // load (e.g., dead a0.muscache.com hotlinks). On error, we hop to the
  // next image that hasn't been marked dead. If everything is dead we
  // show a graceful placeholder instead of a broken-image icon.
  const [failedIdx, setFailedIdx] = useState(() => new Set());
  // Cache of image URLs we've warmed so we only preload each once.
  const warmedRef = useRef(new Set());

  // Preload adjacent images when the current one changes. Using new Image()
  // off-DOM hits the HTTP cache without React rendering, so the next swipe
  // has zero network latency for images the user is about to see.
  useEffect(() => {
    if (!property?.images?.length) return;
    const total = property.images.length;
    const neighbours = new Set([
      (currentImageIndex + 1) % total,
      (currentImageIndex - 1 + total) % total,
    ]);
    neighbours.forEach((i) => {
      const url = thumbUrl(property.images[i]);
      if (!url || warmedRef.current.has(url)) return;
      warmedRef.current.add(url);
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }, [currentImageIndex, property?.images]);

  // On hover we warm the ENTIRE carousel in the background so clicking any
  // dot / arrow from that point on is a cache hit.
  const handleHover = () => {
    if (!property?.images?.length) return;
    property.images.forEach((src) => {
      const url = thumbUrl(src);
      if (!url || warmedRef.current.has(url)) return;
      warmedRef.current.add(url);
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    toggleFavorite();
  };

  const handleImageNavigation = (e, direction) => {
    e.stopPropagation();
    if (direction === 'next') {
      setCurrentImageIndex((prev) =>
        prev === property.images.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === 0 ? property.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <div
      onClick={() => {
        if (onClick && property._id) {
          console.log('PropertyCard clicked:', property._id);
          onClick();
        }
      }}
      onMouseEnter={handleHover}
      onTouchStart={handleHover}
      className="cursor-pointer group transition-transform duration-200 hover:scale-[1.02]"
    >
      {/* Image Container */}
      <div className="relative aspect-square rounded-xl overflow-hidden mb-3 bg-gray-100">
        {(() => {
          const images = property.images || [];
          const allDead = images.length === 0 || failedIdx.size >= images.length;
          const src = allDead
            ? PLACEHOLDER_IMG
            : thumbUrl(images[currentImageIndex]);
          return (
            <img
              src={src}
              alt={property.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={(e) => {
                // a0.muscache.com (Airbnb's CDN) hotlinks frequently 403;
                // skip past the dead URL to the next image, or fall back
                // to a placeholder if all images in this listing are dead.
                if (e.currentTarget.src === PLACEHOLDER_IMG) return;
                setFailedIdx((prev) => {
                  const next = new Set(prev);
                  next.add(currentImageIndex);
                  return next;
                });
                // Advance to the first non-dead image (or stay on placeholder).
                const nextIdx = images.findIndex(
                  (_, i) => i !== currentImageIndex && !failedIdx.has(i),
                );
                if (nextIdx >= 0) setCurrentImageIndex(nextIdx);
              }}
            />
          );
        })()}

        {/* Favorite Button */}
        <Button
          type="button"
          onClick={handleFavoriteClick}
          variant="ghost"
          size="icon"
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/90 hover:bg-white hover:scale-110 transition-all duration-200"
        >
          <Heart
            className={`w-4 h-4 transition-colors ${
              isFavorite ? 'fill-rose-500 text-rose-500' : 'text-gray-700'
            }`}
          />
        </Button>

        {/* Image Navigation Dots */}
        {property.images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1">
            {property.images.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-200 ${
                  index === currentImageIndex
                    ? 'w-6 bg-white'
                    : 'w-1.5 bg-white/60'
                }`}
              />
            ))}
          </div>
        )}

        {/* Navigation Buttons (Show on hover) */}
        {property.images.length > 1 && (
          <>
            <Button
              type="button"
              onClick={(e) => handleImageNavigation(e, 'prev')}
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/90 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </Button>
            <Button
              type="button"
              onClick={(e) => handleImageNavigation(e, 'next')}
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/90 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </Button>
          </>
        )}
      </div>

      {/* Property Details */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 truncate pr-2">
            {property.location}
          </h3>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-current text-gray-900" />
            <span className="text-sm font-medium text-gray-900">{property.rating}</span>
          </div>
        </div>
        <p className="text-gray-600 text-sm truncate">{property.title}</p>
        <p className="text-gray-500 text-sm">
          {t('booking.guestsCount', { count: property.guests })} · {property.bedrooms} {t('property.bedrooms').toLowerCase()}
        </p>
        <div className="pt-1">
          <span className="font-semibold text-gray-900">CA${property.price}</span>
          <span className="text-gray-600 text-sm"> {t('common.night')}</span>
        </div>

        {/* Perk/structure badges — off-white pills with navy text */}
        {getPropertyBadges(property).length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1.5" data-testid="property-badges">
            {getPropertyBadges(property).map((b) => (
              <span
                key={b.key}
                className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                style={{
                  backgroundColor: '#F8F5EE',
                  color: '#1E3F70',
                  borderColor: 'rgba(30, 63, 112, 0.20)',
                }}
                data-testid={`property-badge-${b.key}`}
              >
                {b.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
